import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineChevronRight, MdOutlineChevronLeft } from "react-icons/md";

const Pagination = (props) => {
	const [page, setPage] = useState(1);
	const [pageInput, setPageInput] = useState("1");
	const { t } = useTranslation();

	React.useEffect(() => {
		if (props.currentPage != null && props.currentPage !== page) {
			setPage(props.currentPage);
			setPageInput(String(props.currentPage));
		}
	}, [props.currentPage]);

	const handleClickPageNumber = (pageSelected) => {
		props.changePage(pageSelected);
		setPage(pageSelected);
		setPageInput(String(pageSelected));
	};

	const handleClickFirst = () => {
		props.changePage(1);
		setPage(1);
		setPageInput("1");
	};

	const handleClickLast = () => {
		if (!props.maxPage) return;
		props.changePage(props.maxPage);
		setPage(props.maxPage);
		setPageInput(String(props.maxPage));
	};

	const handleClickPrevious = () => {
		if (page > 1) {
			props.changePage(page - 1);
			setPage(page - 1);
			setPageInput(String(page - 1));
		}
	};

	const handleClickNext = () => {
		if (page < props.maxPage) {
			props.changePage(page + 1);
			setPage(page + 1);
			setPageInput(String(page + 1));
		}
	};

	const handleGoToPage = () => {
		if (!pageInput.trim()) return;
		const requestedPage = Number(pageInput);
		if (!Number.isFinite(requestedPage)) return;
		handleClickPageNumber(
			Math.min(Math.max(Math.trunc(requestedPage), 1), props.maxPage),
		);
	};

	const baseItemClass =
		"flex items-center justify-center min-w-[36px] h-9 px-3 text-sm border border-[var(--borderColor)] rounded-lg cursor-pointer select-none transition-all duration-150 text-[var(--textColor)] hover:bg-[var(--menuHoverColor)] hover:text-[var(--primaryColor)]";

	const disabledItemClass =
		"flex items-center justify-center min-w-[36px] h-9 px-3 text-sm border border-[var(--borderColor)] rounded-lg cursor-not-allowed select-none opacity-40 text-[var(--textColor)]";

	const selectedItemClass =
		"flex items-center justify-center min-w-[36px] h-9 px-3 text-sm border border-[var(--primaryColor)] bg-[var(--primaryColor)] rounded-lg cursor-pointer select-none font-semibold text-white shadow-sm";

	if (props.maxPage <= 1) {
		return <></>;
	}

	const pageNumbers = new Set([page]);
	for (
		let visiblePage = Math.max(1, page - 2);
		visiblePage <= Math.min(props.maxPage, page + 2);
		visiblePage++
	) {
		pageNumbers.add(visiblePage);
	}
	if (page >= 4) pageNumbers.add(1);
	if (props.maxPage - page >= 4) pageNumbers.add(props.maxPage);

	const visiblePages = [];
	let previousPage = 0;
	[...pageNumbers].sort((a, b) => a - b).forEach((visiblePage) => {
		if (previousPage && visiblePage - previousPage > 1) {
			visiblePages.push(
				<li
					key={`ellipsis-${previousPage}`}
					className="px-1 text-(--textColor) opacity-60 select-none"
				>
					&hellip;
				</li>,
			);
		}
		visiblePages.push(
			<li
				key={visiblePage}
				className={visiblePage === page ? selectedItemClass : baseItemClass}
				onClick={() => handleClickPageNumber(visiblePage)}
			>
				{visiblePage}
			</li>,
		);
		previousPage = visiblePage;
	});

	return (
		<div className={`relative flex justify-between items-center mt-4! mb-8!`}>
			{props.total ? (
				<span className="flex-1 text-left text-(--textColor) opacity-50">
					{t("pagination.showingResults", {
						start: (page - 1) * props.rowsPerPage + 1,
						end: Math.min(page * props.rowsPerPage, props.total),
						total: props.total
					})}
				</span>
			) : (
				<div className="flex-1"></div>
			)}
			<div className="flex flex-1 items-center justify-center gap-4">
				<ul className="flex items-center gap-1 list-none p-0 m-0">
					{/* First */}
					<li
						className={page === 1 ? disabledItemClass : baseItemClass}
						onClick={page > 1 ? handleClickFirst : undefined}
						title={t("pagination.firstPage")}
					>
						<MdOutlineChevronLeft size={14} />
						<MdOutlineChevronLeft size={14} className="-ml-2" />
					</li>

					{/* Previous */}
					<li
						className={page === 1 ? disabledItemClass : baseItemClass}
						onClick={page > 1 ? handleClickPrevious : undefined}
						title={t("pagination.previousPage")}
					>
						<MdOutlineChevronLeft size={14} />
					</li>

					{visiblePages}

					{/* Next */}
					<li
						className={page === props.maxPage ? disabledItemClass : baseItemClass}
						onClick={page < props.maxPage ? handleClickNext : undefined}
						title={t("pagination.nextPage")}
					>
						<MdOutlineChevronRight size={14} />
					</li>

					{/* Last */}
					<li
						className={page === props.maxPage ? disabledItemClass : baseItemClass}
						onClick={page < props.maxPage ? handleClickLast : undefined}
						title={t("pagination.lastPage")}
					>
						<MdOutlineChevronRight size={14} />
						<MdOutlineChevronRight size={14} className="-ml-2" />
					</li>
				</ul>
				<label className="flex items-center gap-2 whitespace-nowrap text-sm text-(--textColor) opacity-70">
					{t("pagination.goTo")}
					<input
						type="number"
						min={1}
						max={props.maxPage}
						step={1}
						value={pageInput}
						onChange={(e) => setPageInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								handleGoToPage();
							}
						}}
						className="w-16 h-9 rounded-lg border border-(--borderColor) bg-transparent px-2 text-center text-(--textColor) opacity-100"
						aria-label={t("pagination.goTo")}
					/>
				</label>
			</div>
			<div className="flex-1 flex justify-end">
				{props.changeRowsPerPage && (
					<select
						className="border border-(--borderColor) rounded-lg cursor-pointer select-none px-3 py-2 text-(--textColor) opacity-50"
						value={props.rowsPerPage}
						onChange={(e) => {
							props.changeRowsPerPage(parseInt(e.target.value));
							handleClickPageNumber(1);
						}}
					>
						<option value={6}>{t("pagination.qtPerPage", { qt: 6 })}</option>
						<option value={12}>{t("pagination.qtPerPage", { qt: 12 })}</option>
						<option value={24}>{t("pagination.qtPerPage", { qt: 24 })}</option>
						<option value={48}>{t("pagination.qtPerPage", { qt: 48 })}</option>
						<option value={96}>{t("pagination.qtPerPage", { qt: 96 })}</option>
					</select>
				)}
			</div>
		</div>
	);
};

export default Pagination;
